/**
 * Transfer Group actions renderer
 */
import { Component, ViewEncapsulation } from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  ITransferData,
  COMMON_ACTION_TYPES,
  IDocumentOperationOptions,
  NotificationType,
  ISite,
  ENTITY_TYPES,
  IFilterQuery,
  ISiteResponse,
  EntityId,
} from '@shared/types';

import {
  get,
  forEach,
  cloneDeep,
  isEqual,
  merge,
  isEmpty,
  first,
  filter,
  includes,
  isUndefined,
  map,
} from 'lodash';
import {
  SitesService,
  StateService,
  TransferGroupService,
  TaskService,
  TransferGroupTemplateService,
} from '@shared/services';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import {
  TRANSFER_ACTION_BUTTONS,
  TRANSFER_ACTION_TYPES,
} from '@shared/types/entities/transfer/transfer-action-buttons.enum';

import { XVITransferRendererComponent } from '../transfer-actions-renderer/transfer-actions-renderer.component';
import { MAX_SITES_LIMIT } from '@app/shared/services/transfer-group/transfer-group.constants';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_WARNING_MESSAGE,
} from '@app/shared/constants';
import { rx, observableOf } from '@shared/rx';
import { isSiteTypeWarehouse } from '@shared/services/transfer/transfer.util';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import { GROUP_AUTO_RESTOCK_ROUTE } from '@app/shared/services/transfer-group-auto-restock/transfer-group-auto-restock.constants';
import { isGroupAutoRestockPage } from '@app/shared/utils/transfer-group.auto-restock.utils';
import { saveAsTemplateModalOptions } from '@app/shared/services/transfer-group/save-as-a-template-modal.definition';
import { MAX_PAGE_SIZE } from '@config/global-constants';
import { I18n } from '@app/i18n/strings-keys';
import { getTotalsDefaultValue } from '@app/shared/services/transfer/transfer-pdf-config';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-transfer-actions-renderer',
  templateUrl: 'transfer-group-actions-renderer.component.html',
  styleUrls: ['transfer-group-actions-renderer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XVITransferGroupRendererComponent
  extends XVITransferRendererComponent
  implements AgRendererComponent
{
  @lazyInject(TransferGroupService)
  public transferGroupService: TransferGroupService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(TaskService) public taskService: TaskService;
  @lazyInject(TransferGroupTemplateService)
  public transferGroupTemplateService: TransferGroupTemplateService;

  public data: ITransferData;
  public actionButtons: ActionButton[];

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.transferGroupService.config.ENTITY_TYPE;
    const previousRequest = this.stateService.getByPath(
      ['entities', entityType, 'previousRequest'],
      defaultRequestParams,
    );

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public async edit(): Promise<void> {
    const uiRoute = isGroupAutoRestockPage(this.data)
      ? GROUP_AUTO_RESTOCK_ROUTE
      : this.transferGroupService.config.uiRoute;

    this.router.navigate([uiRoute, get(this, 'data._id', '')]);
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Print:
      case COMMON_ACTION_TYPES.Export:
        this.printOrExport(type);
        break;
      case TRANSFER_ACTION_TYPES.PrintWrittenSheet:
        this.printEmptyColumnPdf();
        break;
      case TRANSFER_ACTION_TYPES.SaveAsTemplate:
        try {
          this.saveAsTemplate();
        } catch (error) {
          this.uiActions.disableLoader();
        }
        break;
      default:
        break;
    }
  }

  protected initActionButtons(): void {
    this.actionButtons = cloneDeep(TRANSFER_ACTION_BUTTONS) as ActionButton[];
    const isTransferGroup = isUndefined(this.data.transfer_group_type);

    forEach(this.actionButtons, (btn) => {
      switch (btn.type) {
        case COMMON_ACTION_TYPES.View:
        case COMMON_ACTION_TYPES.Print:
        case COMMON_ACTION_TYPES.Export:
        case TRANSFER_ACTION_TYPES.PrintWrittenSheet:
          btn.visible = true;
          break;
        case COMMON_ACTION_TYPES.Edit:
        case COMMON_ACTION_TYPES.Submit:
        case COMMON_ACTION_TYPES.Cancel:
        case COMMON_ACTION_TYPES.Reject:
        case COMMON_ACTION_TYPES.Accept:
        case COMMON_ACTION_TYPES.Void:
          btn.visible = false;

          break;
        case TRANSFER_ACTION_TYPES.SaveAsTemplate:
          btn.visible = isTransferGroup;
          break;
        default:
          break;
      }
    });
  }

  public getQohFromVisible(): Promise<boolean> {
    if (isGroupAutoRestockPage(this.data)) {
      // auto restore
      return observableOf(
        this.stateService.getTransferDisplayQohFrom(),
      ).toPromise();
    }

    return this.sitesService
      .fetchList({
        filter: `_id eq objectid(${this.data.site_id})`,
        select: ['type', 'business_type'],
      })
      .pipe(
        rx.map((response) => {
          let isQohFromVisible = true;
          const isWarehouseSite = isSiteTypeWarehouse(
            first(response.items as ISite[]),
          );

          if (isWarehouseSite) {
            isQohFromVisible = this.stateService.getTransferDisplayQohFrom();
          }

          return isQohFromVisible;
        }),
        rx.first(),
      )
      .toPromise();
  }

  public async printHandler(
    isWrittenSheet: boolean = false,
    isExport: boolean = false,
  ): Promise<void> {
    const isQohFromVisible = await this.getQohFromVisible();
    const currentDocument =
      await this.transferGroupService.getDocumentByIdWithSkipUpdateDocument(
        this.data._id,
        merge(<IDocumentOperationOptions>{
          query: {
            include_transfer: true,
            transfer_fields_select:
              this.transferGroupService.transferFieldsSelect.join(','),
          },
          skipMappers: true,
        }),
      );
    const errorMessage = isWrittenSheet
      ? I18n.ErrorMessages.NoAvailableTransferToPrintWrittenSheet
      : I18n.ErrorMessages.NoAvailableTransferForExportOrPrint;
    const bePrintedItems: ITransferData[] = isWrittenSheet
      ? filter(currentDocument.transfer_items, (transferItem) =>
          includes(
            [TRANSFER_STATUS_VALUE.Pending, TRANSFER_STATUS_VALUE.Open],
            transferItem.status,
          ),
        )
      : currentDocument.transfer_items;

    if (isEmpty(bePrintedItems)) {
      this.uiActions.showToasterMessage({
        type: NotificationType.WARNING,
        title: TOASTER_WARNING_MESSAGE.title,
        body: errorMessage,
      });

      return;
    }

    const pdfGenerateFunc =
      this.transferGroupService.transferService.bulkGenerateTransferSummary(
        isWrittenSheet,
        isExport,
        {
          isTransferGroup: true,
          printAndExportMaxLength: MAX_SITES_LIMIT,
          showQohFromOverride: isQohFromVisible,
          totalsConfiguration: {
            enabled: true,
            getDefaultValue: () => getTotalsDefaultValue(bePrintedItems),
          },
        },
      );

    await pdfGenerateFunc(
      this.transferGroupService.addAdditionalInfoToTransfer(
        bePrintedItems,
        this.data.name,
      ),
      this.taskService.getPdfBulkDataWithSyncLoaderFunc(false),
    );
  }

  public printOrExport(
    type: COMMON_ACTION_TYPES.Print | COMMON_ACTION_TYPES.Export,
  ): Promise<void> {
    return this.printHandler(false, isEqual(type, COMMON_ACTION_TYPES.Export));
  }

  public async printEmptyColumnPdf(): Promise<void> {
    return this.printHandler(true, false);
  }

  public async saveAsTemplate(): Promise<void> {
    const { confirmed, value } = await this.uiActions.promptFormModal(
      saveAsTemplateModalOptions(this.transferGroupTemplateService),
    );

    if (!confirmed) {
      return;
    }
    this.uiActions.enableLoader();
    let options = await this.transferGroupTemplateService.getNewTemplatePayload(
      value.name,
      this.data._id,
    );
    const allSiteIds = await this.getAllSiteIds();

    const isFromSiteIsDeleted = !includes(allSiteIds, options.site_id);

    if (isFromSiteIsDeleted) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: I18n.ErrorMessages.FromSiteNotAvailableErrorMessage,
      });
      this.uiActions.disableLoader();

      return;
    }

    if (!isEmpty(options.site_ids_to)) {
      const filteredSiteIds = this.filterSiteIdsToWhenSiteWereDeleted(
        options.site_ids_to,
        allSiteIds,
      );

      // update options,use filtered site to ids.
      options = {
        ...options,
        site_ids_to: filteredSiteIds,
      };
    }

    await this.apiActions.createEntityAsPromised(
      ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE,
      options,
      { noLoader: true },
    );

    this.uiActions.disableLoader();
  }

  public async getAllSiteIds(): Promise<EntityId[]> {
    const queryWithDefaults: IFilterQuery = {
      filter: `is_active eq true`,
      select: ['id'],
      include_inactive: true,
      filter_by_assigned_sites: false,
      top: MAX_PAGE_SIZE,
    };

    return this.sitesService
      .fetchList(queryWithDefaults, { noLoader: true })
      .pipe(
        rx.first(),
        rx.map((response: ISiteResponse): EntityId[] =>
          map(response.items, 'id'),
        ),
      )
      .toPromise();
  }

  public filterSiteIdsToWhenSiteWereDeleted(
    siteIdsTo: EntityId[],
    allSiteIds: EntityId[],
  ): EntityId[] {
    return filter(siteIdsTo, (siteId: EntityId): boolean =>
      includes(allSiteIds, siteId),
    );
  }
}
